import PropTypes from "prop-types";
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
} from "@mui/material";
import {
  Search as SearchIcon,
  OpenInNew,
  LinkOutlined,
} from "@mui/icons-material";
import Spinner from "../../../components/Spinner.jsx";
import TabList from "../../../components/TabList.jsx";
import { ListContainer, ListItem } from "./lists.jsx";
import { borderRadius, colorsFoundation } from "../../../theme/index.js";
import typography from "../../../theme/tokens/typography.js";
import {
  formatDatePeriod,
  getDatumZeit,
  getEuro,
} from "../../../services/formatService.js";

const PaymentMatchingPaymentsPanel = ({
  paymentImport,
  paymentsSearchword,
  onPaymentsSearchwordChange,
  paymentFilter,
  onPaymentFilterChange,
  filteredPayments = [],
  loadingPayments,
  selectedClaim,
  addPaymentToPossibleMatches,
}) => {
  const sortedPayments = [...filteredPayments].sort(
    (a, b) => new Date(b.processed_at) - new Date(a.processed_at)
  );

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: borderRadius.md,
        border: `1px solid ${colorsFoundation.neutral[200]}`,
        minHeight: 0,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          mb: 2,
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Typography
          variant="h6"
          sx={{ fontSize: typography.size.xl, m: 0 }}
          gutterBottom
        >
          Zahlungen
        </Typography>
        {paymentImport && (
          <Typography variant="body2" color="text.secondary">
            <span>Zeitraum:</span>{" "}
            {formatDatePeriod(paymentImport.from_date, paymentImport.to_date, {
              format: "short",
            })}
          </Typography>
        )}
      </Box>
      <Box sx={{ mb: 2 }}>
        <TextField
          id="payment-matching-payment-search"
          placeholder="Zahlungen suchen..."
          value={paymentsSearchword}
          onChange={(event) => onPaymentsSearchwordChange(event.target.value)}
          fullWidth
          size="small"
          variant="outlined"
          label="Zahlung suchen"
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
      </Box>
      <Box sx={{ mb: 2 }}>
        <TabList
          tabs={[
            { key: "all", label: "Alle" },
            { key: "unassigned", label: "Nicht zug." },
            { key: "assigned", label: "Zugeordnet" },
          ]}
          activeTab={paymentFilter}
          onTabChange={onPaymentFilterChange}
          containerSx={{
            mt: 0,
            borderBottom: `1px solid ${colorsFoundation.neutral[200]}`,
          }}
        />
      </Box>
      <Box sx={{ flex: 1, overflow: "auto" }}>
        {loadingPayments ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
            <Spinner size={24} />
          </Box>
        ) : (
          <ListContainer>
            {sortedPayments.map((payment) => {
              const isAssigned = Boolean(payment.booking_id);

              return (
                <ListItem
                  key={payment.id}
                  onClick={
                    !isAssigned && selectedClaim && payment.debit_credit === "C"
                      ? () => addPaymentToPossibleMatches(payment)
                      : undefined
                  }
                  sx={{
                    backgroundColor: isAssigned
                      ? colorsFoundation.status.positive[50]
                      : "inherit",
                    borderLeft: isAssigned
                      ? `4px solid ${colorsFoundation.status.positive[500]}`
                      : "4px solid transparent",
                    cursor:
                      !isAssigned &&
                      selectedClaim &&
                      payment.debit_credit === "C"
                        ? "pointer"
                        : "default",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                    }}
                  >
                    {isAssigned && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            px: 1,
                            py: 0.25,
                            borderRadius: 999,
                            fontSize: "0.75rem",
                            backgroundColor:
                              colorsFoundation.status.positive[100],
                            color: colorsFoundation.status.positive[700],
                          }}
                        >
                          Zugeordnet
                        </Box>
                        {payment.booking?.schaden_id && (
                          <IconButton
                            size="small"
                            onClick={(event) => {
                              event.stopPropagation();
                              window.open(
                                `/schaden/${payment.booking.schaden_id}?tab=buchungen`,
                                "_blank"
                              );
                            }}
                          >
                            <OpenInNew fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    )}
                    <Typography variant="caption" color="text.secondary">
                      {payment.remitter}
                    </Typography>
                    <Typography variant="body1">
                      {payment.description}
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mt: 1,
                        alignItems: "center",
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Datum: {getDatumZeit(payment.processed_at)}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color:
                            payment.debit_credit === "C"
                              ? "success.main"
                              : "error.main",
                        }}
                      >
                        {payment.debit_credit === "C" ? "" : "-"}
                        {getEuro(payment.amount)}
                      </Typography>
                    </Box>
                  </Box>
                </ListItem>
              );
            })}

            {sortedPayments.length === 0 && (
              <Box sx={{ textAlign: "center", py: 8 }}>
                <Typography variant="body1" color="text.secondary">
                  Keine offenen Zahlungen vorhanden
                </Typography>
              </Box>
            )}
          </ListContainer>
        )}
      </Box>
    </Paper>
  );
};

PaymentMatchingPaymentsPanel.propTypes = {
  paymentImport: PropTypes.shape({
    from_date: PropTypes.string,
    to_date: PropTypes.string,
  }),
  paymentsSearchword: PropTypes.string.isRequired,
  onPaymentsSearchwordChange: PropTypes.func.isRequired,
  paymentFilter: PropTypes.string.isRequired,
  onPaymentFilterChange: PropTypes.func.isRequired,
  filteredPayments: PropTypes.arrayOf(PropTypes.object),
  loadingPayments: PropTypes.bool.isRequired,
  selectedClaim: PropTypes.object,
  addPaymentToPossibleMatches: PropTypes.func.isRequired,
};

export default PaymentMatchingPaymentsPanel;
